import { formatCurrency as sharedFormatCurrency } from '@erp/shared-utils';
import Decimal from 'decimal.js';
import type { DiscountValidationResult } from 'src/types/application/SalesOrderDraftDTO';

/**
 * Get the validated/ERP unit price from discount validation results.
 * Returns discount price if available, otherwise list price.
 */
export function getErpUnitPrice(
  discountResult?: DiscountValidationResult,
): number | null {
  // Use nullish check (not truthy) so that discountPrice of 0 is accepted
  if (discountResult?.discount?.discountPrice != null) {
    return discountResult.discount.discountPrice;
  }
  return discountResult?.listPrice?.price ?? null;
}

/**
 * Get the Oracle list price from discount validation results.
 */
export function getListPrice(
  discountResult?: DiscountValidationResult,
): number | null {
  return discountResult?.listPrice?.price ?? null;
}

/**
 * Calculate discount percentage from list price.
 * Returns the percentage discount (0-100 scale).
 * A positive value means the unit price is below list price.
 */
export function calculateDiscountPercent(
  unitPrice: number | null | undefined,
  listPrice: number | null | undefined,
): number | null {
  if (
    unitPrice == null ||
    listPrice == null ||
    listPrice === 0 ||
    isNaN(unitPrice) ||
    isNaN(listPrice)
  ) {
    return null;
  }
  return Math.max(0, ((listPrice - unitPrice) / listPrice) * 100);
}

/** Fallback currency code when none is provided or the provided one is invalid. */
const DEFAULT_CURRENCY = 'USD';

/**
 * Normalise a potentially empty/null/undefined currency code to a safe value.
 */
function safeCurrency(code: string | null | undefined): string {
  return code && code.trim().length > 0 ? code.trim() : DEFAULT_CURRENCY;
}

/**
 * Format a number as currency using the proper symbol for the given currency code.
 * Delegates to the shared Intl.NumberFormat-based formatter.
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currencyCode?: string | null,
): string {
  if (value == null) {
    return '-';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '-';
  }
  return sharedFormatCurrency(numValue, safeCurrency(currencyCode));
}

/**
 * Get the currency symbol for a given currency code (e.g. "$" for USD, "€" for EUR).
 */
export function getCurrencySymbol(currencyCode?: string | null): string {
  const code = safeCurrency(currencyCode);
  try {
    return (
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
      })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? '$'
    );
  } catch {
    return '$';
  }
}

/**
 * Format a number as a percentage with one decimal place.
 */
export function formatPercent(value: number | null | undefined): string {
  if (value == null || isNaN(value)) {
    return '-';
  }
  return `${value.toFixed(1)}%`;
}

/**
 * Calculate total price (quantity × unit price).
 *
 * The unit price is rounded to cent precision **before** multiplying so
 * the displayed total matches the rounded unit-price column instead of
 * drifting from a high-precision underlying value (a Fusion-extracted
 * price like `92.0375784` would otherwise multiply through and produce
 * `$16,566.76` for a quantity of 180 while the unit-price column reads
 * `$92.04` — Fusion does its own 2dp rounding on the receive side, so
 * lining the calculation up here keeps both totals in agreement).
 *
 * Uses `decimal.js` to avoid IEEE-754 mis-rounding on half-cent values
 * (e.g. `1.005` would round down with `Math.round`). Matches the
 * rounding pattern used elsewhere in the financial codebase.
 */
export function calculateTotal(
  quantity: number | null | undefined,
  unitPrice: number | null | undefined,
): number | null {
  if (
    quantity == null ||
    unitPrice == null ||
    isNaN(quantity) ||
    isNaN(unitPrice)
  ) {
    return null;
  }
  const unitPriceInCents = new Decimal(unitPrice).toDecimalPlaces(2).toNumber();
  return quantity * unitPriceInCents;
}
